<template>
  <v-container
    class="pa-2"
    style="max-width: 1920px"
  >
    <preview
      id="preview-project"
      :controllers-project="[creationController]"
      :is-preview="true"
      :reset.sync="reset"
      :show="showPreview"
    />
    <v-row>
      <v-col>
        <v-row justify="start">
          <v-col>
            <v-toolbar ref="toolbar">
              <v-row>
                <standard-options
                  ref="standardOptions"
                  :project-name="computedProjectName"
                  :project-duration="projectDuration"
                  @load-file="loadFile"
                  @saveFile="saveFile"
                />
                <v-divider
                  vertical
                  class="my-1"
                />
                <geometry-options
                  :partial="isPartialRectOption"
                  :is-object-created="creationController.currentElement != null"
                  :object-created="currentObjectUpdating"
                  :current-transformer="creationController.currentSelection"
                  :creation-controller="creationController"
                  @onFocus="onFocus"
                />
                <v-divider
                  v-if="creationController.currentElement != null"
                  vertical
                  class="my-1"
                />
                <position-options
                  :partial="isPartialRectOption"
                  :is-object-created="creationController.currentElement != null"
                  :object-created.sync="currentObjectUpdating"
                  :current-transformer="creationController.currentSelection"
                  :creation-controller="creationController"
                />
              </v-row>
            </v-toolbar>
          </v-col>
        </v-row>
        <v-row align="stretch">
          <v-col
            class="creation-toolbar pt-0"
            style="overflow: hidden"
            cols="1"
          >
            <left-toolbar
              ref="leftToolbar"
              :current-object="computedCurrentElement"
              :size-project="creationController.projectSize"
              @editElement="editElement"
              @toolSelected="toolSelected"
              @mainColor="mainColorSelected"
              @secondaryColor="secondaryColorSelected"
              @projectPreview="startPreview"
              @startProjectPreviewAnimations="startProjectPreviewAnimations"
              @cloned="cloned"
            />
          </v-col>
          <v-col
            ref="creationPageWorkspaceContainer"
            v-resize="onResize"
            class="creation-page-workspace-container"
            @mouseleave="stageMouseLeave"
          >
            <v-stage
              ref="creationPageWorkspace"
              class="creation-page-workspace"
              :config="configKonva"
              @wheel="zoom"
              @mousedown="stageMouseDown"
              @mouseup="stageMouseUp"
              @mouseenter="isCanvasActive = true"
              @mouseleave="isCanvasActive = false"
            />
          </v-col>
          <v-col
            cols="3"
            class="pt-0"
          >
            <right-panel
              :layers.sync="layers"
              :element="computedCurrentElement"
              :layer-selected="layerSelected"
              @edit-changed="editChanged"
              @layers-moved="layersMoved"
              @move-changed="moveChanged"
              @select-layer="selectLayer"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row id="textRenderingToCanvas" />
    <v-row>
      <v-col>
        <div v-if="getIsLoading">
          <v-subheader>
            {{ $t("creation.tools.loading") }}
          </v-subheader>
          <v-progress-linear
            color="#c0c0c0"
            indeterminate
            rounded
            height="6"
          />
        </div>
      </v-col>
    </v-row>
    <dialog-leave
      ref="dialogLeave"
      :btn-txt1="$t('dialog.save')"
      :btn-txt2="$t('dialog.not-save')"
      :btn-txt3="$t('dialog.cancel')"
      :msg="$t('dialog.save-project-label')"
      :subtitle="$t('dialog.save-subtitle')"
      :title="$t('appname')"
    />
  </v-container>
</template>

<script>
// Components
import leftToolbar from "./CreationPage/LeftToolbar";
import rightPanel from "./CreationPage/RightPanel";
import geometryOptions from "./CreationPage/EditTools/Options/GeometryOptions";
import positionOptions from "./CreationPage/EditTools/Options/PositionOptions";
import standardOptions from "./CreationPage/EditTools/Options/StandardOptions";
import dialogLeave from "./Dialog/DialogLeave";

// Controllers
import { CreationController } from "./CreationPage/CreationController";
import { ProjectSaver } from "./CreationPage/SaveManager/ProjectSaver";
import { ProjectLoader } from "./CreationPage/SaveManager/ProjectLoader";
import { Project } from "./MainObjects/Project";

// Connectors
import { ProjectConnector } from "./Database/ProjectConnector";
import { TemplateConnector } from "./Database/TemplateConnector";

function checkValueInt(value, defaultValue) {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    return defaultValue;
  }
  return parsed;
}

function checkValueFloat(value) {
  const parsed = parseFloat(value);
  if (isNaN(parsed)) {
    return 0.01;
  }
  return parsed;
}

export default {
  components: {
    leftToolbar,
    standardOptions,
    geometryOptions,
    positionOptions,
    rightPanel,
    dialogLeave,
  },
  props: {
    projectName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      baseConfigKonva: {
        x: 0,
        y: 0,
        width: 1920,
        height: 1080,
        scale: {
          x: this.scale,
          y: this.scale,
        },
      },
      contentOfTextOption: null,
      creationController: new CreationController(),
      currentObjectOption: {},
      deleted: false,
      leaveResponse: "0",
      isInputFocused: false,
      isStageMoving: false,
      layers: [],
      mouseDown: false,
      showPreview: false,
      project: new Project(),
      projectLoader: null,
      projectDuration: 10,
      reset: false,
      scale: 1,
      unsavedChange: false,
      saveInfo: "",
      thumbnail: "",
      templatePath: "share/template/private",
      isCanvasActive: false,
    };
  },
  computed: {
    getIsLoading() {
      const isLoading = this.$store.state.project.isLoading;

      if (isLoading) {
        document.body.style.cursor = "wait";
      } else {
        document.body.style.cursor = "default";
      }

      return isLoading;
    },
    computedCurrentElement() {
      if (this.creationController.currentElement != null) {
        if (this.isCurrentMovable()) {
          this.creationController.enableDraggable();
        } else {
          this.creationController.disableDraggable();
        }
      }

      return this.creationController.currentElement;
    },
    computedProjectName: {
      get() {
        return this.creationController.projectName;
      },
      set(val) {
        this.creationController.projectName = val;
      },
    },
    configKonva() {
      return this.baseConfigKonva;
    },
    currentObjectUpdating() {
      if (this.currentObjectOption !== undefined && this.currentObjectOption != {}) {
        if (
          this.creationController.stage !== undefined &&
          this.creationController.stage.container().style.cursor == "default" &&
          !this.deleted &&
          this.creationController.currentElement !== null
        ) {
          this.creationController.stage.container().style.cursor = "grab";
        }
        this.updateCurrentObjectFromOptions(
          this.currentObjectOption.x,
          this.currentObjectOption.y,
          this.currentObjectOption.width,
          this.currentObjectOption.height,
          this.currentObjectOption.scaleX,
          this.currentObjectOption.scaleY
        );
      }

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.deleted = false;

      return this.currentObjectOption;
    },
    currentOptionsTool() {
      if (this.creationController === undefined) {
        return [];
      }
      return this.creationController.currentOptionDisplayed;
    },
    layerSelected() {
      if (this.currentObjectOption != null && this.currentObjectOption !== undefined) {
        return this.currentObjectOption.id;
      }
      return undefined;
    },
    isImageOption() {
      if (this.currentOptionsTool == undefined) {
        return;
      }
      return this.currentOptionsTool.indexOf("Image") != -1;
    },
    isRectOption() {
      if (this.currentOptionsTool == undefined) {
        return;
      }
      return this.currentOptionsTool.indexOf("Rect") != -1 || this.currentOptionsTool.indexOf("RectPartial") != -1;
    },
    isPartialRectOption() {
      if (this.currentOptionsTool == undefined) {
        return;
      }
      return this.currentOptionsTool.indexOf("RectPartial") != -1;
    },
    isTextOption() {
      if (this.currentOptionsTool == undefined) {
        return;
      }
      return this.currentOptionsTool.indexOf("Text") != -1;
    },
    stage() {
      return this.$refs.creationPageWorkspace.getStage();
    },
  },
  created() {
    document.title = this.$t("navbar.creation") + " · " + this.$t("appname");
    this.$store.dispatch("setHeader", this.$t("navbar.creation"));
  },
  //This is where the creation controller is created. It loads or creates a new project according to how you came to this page.
  mounted() {
    if (this.$store.state.user.socId != null) {
      this.$nextTick(function () {
        this.manageNewProject();
        this.setupKeys();
        this.onResize();

        try {
          this.stage.draw();
        } catch (err) {
          console.error(err);
        }
      });
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.unsavedChange) {
      // Wait for dialog response before
      const res = await this.dialogLeaveResponse();
      switch (res) {
        case 1:
          next(false);
          this.$refs.standardOptions.openSaveDialog();
          break;
        case 2:
          this.leaveCreation();
          next();
          break;

        default:
          next(false);
          break;
      }
    } else {
      this.leaveCreation();
      next();
    }
  },
  methods: {
    leaveCreation() {
      window.removeEventListener("keydown", this.keyDownEvent);
      window.removeEventListener("keyup", this.keyUpEvent);
      this.creationController.destroyAll();
    },
    editChanged(value) {
      if (this.creationController.currentElement != null) {
        this.creationController.currentElement.konvaObjectOption.editable = value;
      }
    },
    editElement(tool) {
      // Backup of editable and movable properties
      let bakEditable = this.isCurrentEditable();
      let bakMovable = this.isCurrentMovable();

      // Retrieve infos
      const pos = this.layers.indexOf(this.creationController.currentElement.konvaObjectOption.id);
      this.deleteElementFromList(this.layers, this.creationController.deleteCurrentSelection());
      this.layers.push(tool.editNewElement(this.creationController));

      // Restore editable and movable properties
      this.creationController.currentElement.konvaObjectOption.editable = bakEditable;
      this.creationController.currentElement.konvaObjectOption.movable = bakMovable;

      this.currentObjectOption = this.creationController.currentElement.konvaObject.attrs;

      this.creationController.currentTool = { name: "pointer", type: "basic" };
      this.creationController.enableDraggable();

      const layer = {
        move: "edit",
        element: this.creationController.currentElement.konvaObjectOption.id,
        pos: pos,
      };

      this.$store.dispatch("layerMoved", layer).creation;
    },
    moveChanged(value) {
      if (this.creationController.currentElement != null) {
        // Current name
        // this.creationController.currentElement.name
        this.creationController.currentElement.konvaObjectOption.movable = value;
      }
    },
    cleanStage() {
      this.stage.destroyChildren();
    },
    deleteOptionsElements() {
      if (this.$refs.toolbar.lastElementChild != null) {
        this.$refs.toolbar.removeChild(this.$refs.toolbar.lastElementChild);
      }
    },
    deleteElementFromList(list, elementName) {
      this.creationController.stage.container().style.cursor = "default";
      this.deleted = true;
      let index = list.indexOf(elementName);
      if (index > -1) {
        list.splice(index, 1);
      }
      this.unsavedChange = true;
    },
    dialogLeaveResponse() {
      return new Promise((resolve) => {
        this.$refs.dialogLeave.openDialog();
        // TODO: WTF watch a refs !!!
        this.$refs.dialogLeave.$watch("response", function (val) {
          resolve(val);
        });
      });
    },
    focusOption(object) {
      this.currentObjectOption = object;
    },
    isCurrentEditable() {
      if (this.creationController.currentElement != null) {
        if (this.creationController.currentElement.konvaObjectOption !== undefined) {
          if (this.creationController.currentElement.konvaObjectOption.editable !== undefined) {
            if (this.creationController.currentElement.konvaObjectOption.editable) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    isCurrentMovable() {
      if (this.creationController.currentElement != null) {
        if (this.creationController.currentElement.konvaObjectOption !== undefined) {
          if (this.creationController.currentElement.konvaObjectOption.movable !== undefined) {
            if (this.creationController.currentElement.konvaObjectOption.movable) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    layersMoved(value) {
      // Create a new object which will contain new order
      let temp_elements = {};
      let zIndex = 0;

      value.forEach((element) => {
        if (this.creationController.elements[element] !== undefined) {
          // Change zIndex of the current creationController
          this.creationController.elements[element].konvaObject.zIndex(zIndex);
          // Add elements in the new object in the right order
          temp_elements[element] = this.creationController.elements[element];

          zIndex++;
        }
      });

      // Swap the objects in creation controller with the right layers order
      this.creationController.elements = temp_elements;

      this.unsavedChange = true;
    },

    onFocus(isFocused) {
      this.isInputFocused = isFocused;
    },
    loadFile(projectData) {
      // WE SHOW LOAD BAR
      this.$store.dispatch("loadStarted").project;

      this.layers = [];
      this.cleanStage();
      this.creationController = new CreationController();
      if (projectData.savePath === undefined || projectData.savePath === "") {
        const project = new Project(
          projectData.path.replace("./", ""),
          projectData.name,
          projectData.duration,
          projectData.id
        );
      }
      const project = new Project(
        projectData.savePath.replace("./", ""),
        projectData.projectName,
        projectData.duration,
        projectData.projId
      );

      const projectConnector = new ProjectConnector(this.$store.state.user);
      const templateConnector = new TemplateConnector(this.$store.state.user);

      // Skip this step if we use a shared project
      if (!project.path.includes("mediacenter/share/")) {
        projectConnector.projectBeingOpened(project);
      }

      // Wait project loading before creating layers
      const projectLoader = new ProjectLoader(
        this.creationController,
        this.$store.state.user,
        projectData,
        this.stage,
        {
          width: this.$refs.creationPageWorkspaceContainer.clientWidth,
          height: this.$refs.creationPageWorkspaceContainer.clientHeight,
        }
      );

      projectLoader
        .load()
        .then((response) => {
          this.computedProjectName = this.creationController.projectName;

          if (projectData.savePath !== undefined) {
            let isTemplate = projectData.savePath.includes("/template");
            if (isTemplate) {
              templateConnector
                .getDuration(projectData.savePath)
                .then((duration) => {
                  this.projectDuration = duration;
                })
                .catch((err) => {
                  console.error(err);
                });
            } else {
              projectConnector
                .getDuration(projectData.savePath)
                .then((duration) => {
                  this.projectDuration = duration;
                })
                .catch((err) => {
                  console.error(err);
                });
            }
          }
          for (const layerName of response) {
            this.layers.push(layerName[0]);
          }
          //this.layersMoved(this.layers);
        })
        .catch((err) => {
          this.$store.dispatch("openSnackbar", {
            title: this.$t("dialog.warning"),
            txt: this.$t("error.open"),
            colorBg: "orange",
          });
          console.error(err);
        })
        .finally(() => {
          const size = {
            width: this.creationController.projectSize.width,
            height: this.creationController.projectSize.height,
          };

          // WE DEFINE PROJECT SIZE
          this.$store.dispatch("setSize", size).project;

          // WE HIDE LOAD BAR
          this.$store.dispatch("loadFinished").project;
        });
    },
    mainColorSelected(color) {
      this.creationController.mainColor = color;
      if (this.creationController.currentElement !== null) {
        if (this.isCurrentEditable()) {
          this.creationController.currentElement.setColor(color);
        }
      }
    },
    manageNewProject() {
      if (this.$store.state.project.isNew) {
        this.creationController.createCompleteController(
          this.stage,
          this.$store.state.project.properties,
          this.$store.state.project.name
        );
      } else {
        this.loadFile(this.$store.state.project.openData);
      }
    },
    startPreview() {
      this.showPreview = true;
    },
    startProjectPreviewAnimations() {
      //animations in (entree)
      const allElements = Object.keys(this.creationController.elements);
      let totalDuration = 0;
      allElements.forEach((elToPlay) => {
        const el = this.creationController.elements[elToPlay];
        el.createAnimation();
        if (el.animObj) {
          let delay = parseInt(el.animDelay) * 1000;
          totalDuration += delay + parseInt(el.animSpeed) * 1000;
          setTimeout(() => {
            el.animObj.play();
          }, delay);
        }
      });
      //animations out (sortie)
      setTimeout(() => {
        let totalDurationOut = 0;
        allElements.forEach((elToPlay) => {
          const el = this.creationController.elements[elToPlay];
          el.createAnimationOut();
          if (el.animOutObj) {
            let delay = parseInt(el.animOutDelay) * 1000;
            setTimeout(() => {
              el.animOutObj.play();
            }, delay);

            totalDurationOut += delay + parseInt(el.animOutSpeed) * 1000;
          }
        });
        //remet les éléments à leur place
        setTimeout(() => {
          allElements.forEach((elToPlay) => {
            this.creationController.elements[elToPlay].reset();
          });
        }, totalDurationOut + 1000);
      }, totalDuration);
    },
    onResize() {
      if (!this.creationController.init) {
        return;
      }
      this.setWorkspaceScale();
      return;
    },
    saveFile(saveData) {
      // BEGIN MAKING THUMBNAIL (MAX. 300 PIXELS)
      let ratio = 300 / this.creationController.projectSize.width;

      if (ratio > 300 / this.creationController.projectSize.height) {
        ratio = 300 / this.creationController.projectSize.height;
      }

      if (this.creationController.projectSize.width <= 300 && this.creationController.projectSize.height <= 300) {
        ratio = 1;
      }

      // REMOVE ALL SELECTION BEFORE MAKING THUMBNAIL
      this.creationController.destroyAllTransformer();
      this.creationController.deselectCurrentElement();

      // STORE ORIGINAL VALUES
      const origX = this.creationController.stage.x();
      const origY = this.creationController.stage.y();
      const origWidth = this.creationController.stage.width();
      const origHeight = this.creationController.stage.height();

      // CALC CANVAS SIZE FOR THUMBNAIL (ADD 10px to get project end)
      const newWidth = this.creationController.projectSize.width * this.creationController.stage.attrs.scaleX + 10;
      const newHeight = this.creationController.projectSize.height * this.creationController.stage.attrs.scaleY + 10;

      // CENTER CANVAS (add 5px on X to center the 10px added earlier)
      this.creationController.stage.x(5);
      this.creationController.stage.y(0);
      this.creationController.stage.width(newWidth);
      this.creationController.stage.height(newHeight);
      this.thumbnail = this.creationController.stage.toDataURL({
        pixelRatio: ratio,
      });

      // RESTORE ORIGINAL VALUES
      this.creationController.stage.x(origX);
      this.creationController.stage.y(origY);
      this.creationController.stage.width(origWidth);
      this.creationController.stage.height(origHeight);

      // SAVE PROJECT
      this.saveInfo = saveData;
      // TODO: Need to add ./ if not exists better option possible ? add it in db ? or useless ?
      if (!this.saveInfo.savePath.startsWith("./")) {
        this.saveInfo.savePath = "./" + this.saveInfo.savePath;
      }

      if (this.saveInfo.isTemplate) this.saveInfo.savePath = this.saveInfo.savePath.replace("1000", this.templatePath);

      const projectSaver = new ProjectSaver(this.creationController, this.$store.state.user);
      projectSaver
        .save(this.saveInfo.savePath)
        .then(() => {
          this.$store.dispatch("openSnackbar", {
            title: this.$t("dialog.info"),
            txt: this.$t("dialog.save-success"),
            colorBg: "green",
          });
        })
        .catch((err) => {
          this.$store.dispatch("openSnackbar", {
            title: this.$t("dialog.warning"),
            txt: this.$t("error.save"),
            colorBg: "red",
          });
          console.error(err);
        });

      const project = new Project(
        this.saveInfo.savePath,
        this.saveInfo.projectName,
        this.saveInfo.duration,
        null,
        null,
        this.$store.state.user.socId,
        this.$store.state.project.properties.width,
        this.$store.state.project.properties.height,
        this.thumbnail,
        this.saveInfo.isTemplate
      );

      const projectConnector = new ProjectConnector(this.$store.state.user);
      projectConnector.projectBeingSaved(project);

      // SET PROJECT NAME IN CREATION CONTROLLER
      this.creationController.name = this.saveInfo.projectName;

      // UNSET UNSAVED CHANGE
      this.unsavedChange = false;
    },
    secondaryColorSelected(color) {
      this.creationController.secondaryColor = color;
    },
    selectLayer(element) {
      this.creationController.destroyAllTransformer();
      this.creationController.deselectCurrentElement();
      if (element !== undefined) {
        this.creationController.currentElement = this.creationController.elements[element];

        if (this.creationController.currentElement.getType() == "Text") {
          this.contentOfTextOption = this.creationController.currentElement.text;
        }

        this.currentObjectOption = this.creationController.currentElement.konvaObject.attrs;
        this.creationController.currentElement.select();
        this.creationController.createTransformerOnCurrentElement();
        this.creationController.getCurrentSelectionOptionsFromId();
      }
    },
    selectWithMouseClick(event) {
      this.creationController.mouseEventTrigger("displayTransformer", event);
      if (this.creationController.currentElement == null) {
        return;
      }

      if (this.creationController.currentElement.getType() == "Text") {
        this.contentOfTextOption = this.creationController.currentElement.text;
      }
      this.creationController.currentElement.select();
      this.currentObjectOption = this.creationController.currentElement.konvaObject.attrs;
      this.creationController.getCurrentSelectionOptionsFromId();
    },
    keyDownEvent(event) {
      if (!this.isCanvasActive) return;
      let value = this.currentObjectUpdating;
      let deltaMove = 1;
      let layer = { move: "", element: "" };

      if (this.creationController.shiftKeyPressed) {
        deltaMove = 10;
      }

      // DISABLE KEY WATCH WHEN A DIALOG IS OPEN
      if (!this.isInputFocused) {
        switch (event.keyCode) {
          case 8: // DELETE KEY
            if (!this.isInputFocused && this.isCurrentMovable() && this.isCurrentEditable()) {
              if (value !== undefined) {
                this.deleteElementFromList(this.layers, this.creationController.deleteCurrentSelection());
              }
            }
            break;
          case 16: // SHIFT KEY
            this.creationController.shiftKeyPressed = true;
            break;
          case 17: // CTRL KEY
            this.creationController.ctrlKeyPressed = true;
            break;
          case 18: // ALT KEY
            this.creationController.altKeyPressed = true;
            break;
          case 27: // ESCAPE KEY
            this.showPreview = false;
            this.reset = true;
            break;
          case 37: // LEFT ARROW KEY
            if (this.isCurrentMovable()) {
              if (value !== undefined) {
                this.updateCurrentObjectFromOptions(
                  value.x - deltaMove,
                  value.y,
                  value.width,
                  value.height,
                  value.scaleX,
                  value.scaleY
                );
              }
            }
            break;
          case 38: // UP ARROW KEY
            if (this.isCurrentMovable()) {
              if (value !== undefined && !this.creationController.ctrlKeyPressed) {
                this.updateCurrentObjectFromOptions(
                  value.x,
                  value.y - deltaMove,
                  value.width,
                  value.height,
                  value.scaleX,
                  value.scaleY
                );
              }
              if (value !== undefined && this.creationController.ctrlKeyPressed) {
                layer.element = this.creationController.currentElement.konvaObjectOption.id;
                layer.move = "top";
                this.$store.dispatch("layerMoved", layer).creation;
              }
            }
            break;
          case 39: // RIGHT ARROW KEY
            if (this.isCurrentMovable()) {
              if (value !== undefined) {
                this.updateCurrentObjectFromOptions(
                  value.x + deltaMove,
                  value.y,
                  value.width,
                  value.height,
                  value.scaleX,
                  value.scaleY
                );
              }
            }
            break;
          case 40: // DOWN ARROW KEY
            if (this.isCurrentMovable()) {
              if (value !== undefined && !this.creationController.ctrlKeyPressed) {
                this.updateCurrentObjectFromOptions(
                  value.x,
                  value.y + deltaMove,
                  value.width,
                  value.height,
                  value.scaleX,
                  value.scaleY
                );
              }
              if (value !== undefined && this.creationController.ctrlKeyPressed) {
                layer.element = this.creationController.currentElement.konvaObjectOption.id;
                layer.move = "bottom";
                this.$store.dispatch("layerMoved", layer).creation;
              }
            }
            break;
          case 46: // DELETE KEY
            if (this.isCurrentMovable() && this.isCurrentEditable()) {
              if (value !== undefined) {
                this.deleteElementFromList(this.layers, this.creationController.deleteCurrentSelection());
              }
            }
            break;
          case 68: // D KEY
            if (this.$refs.leftToolbar !== undefined) {
              this.$refs.leftToolbar.resetColors();
            }
            break;
          case 74: // J KEY
            event.preventDefault();
            if (this.creationController.ctrlKeyPressed) {
              this.cloned(this.creationController.currentElement);
            }
            break;
          case 86: // V KEY
            event.preventDefault();
            if (this.creationController.ctrlKeyPressed) {
              this.cloned(this.creationController.currentElement);
            }
            break;
          case 88: // X KEY
            if (this.$refs.leftToolbar !== undefined) this.$refs.leftToolbar.invertColors();
            break;

          default:
            break;
        }
      }
    },
    keyUpEvent(event) {
      switch (event.keyCode) {
        case 16: // SHIFT KEY
          this.creationController.shiftKeyPressed = false;
          break;
        case 17: // CTRL KEY
          this.creationController.ctrlKeyPressed = false;
          break;
        case 18: // ALT KEY
          this.creationController.altKeyPressed = false;
          event.preventDefault();
          break;

        default:
          break;
      }
    },
    setupKeys() {
      window.addEventListener("keydown", this.keyDownEvent);
      window.addEventListener("keyup", this.keyUpEvent);
    },
    setWorkspaceScale() {
      this.creationController.autoRescaleStage({
        width: this.$refs.creationPageWorkspaceContainer.clientWidth,
        height: this.$refs.creationPageWorkspaceContainer.clientHeight,
      });
    },
    moveStage(event) {
      try {
        this.isStageMoving = true;
        this.creationController.mouseEventTrigger("moveStage", event);
      } catch (err) {
        this.moveStageEnd(event);
      }
    },
    stageMouseDown(event) {
      if (this.getIsLoading) {
        return;
      }

      this.mouseDown = true;
      if (this.creationController.currentTool.type == "basic") {
        switch (event.evt.button) {
          case 0: // LEFT CLICK
            switch (this.creationController.currentTool.name) {
              case "move":
                this.moveStage(event);
                break;
              case "pointer":
                this.selectWithMouseClick(event);
                break;
              case "zoom":
                event.evt.deltaY = -100;
                this.creationController.stage.container().style.cursor = "zoom-in";
                this.creationController.mouseEventTrigger("zoom", event);
                break;

              default:
                break;
            }
            break;
          case 1: // MIDDLE CLICK
            this.creationController.stage.container().style.cursor = "move";
            this.moveStage(event);
            break;
          case 2: // RIGHT CLICK
            switch (this.creationController.currentTool.name) {
              case "zoom":
                event.evt.deltaY = 100;
                this.creationController.stage.container().style.cursor = "zoom-out";
                this.creationController.mouseEventTrigger("zoom", event);
                break;

              default:
                break;
            }
            break;

          default:
            break;
        }
      } else if (this.creationController.currentTool.type == "widget") {
        switch (event.evt.button) {
          case 0: // LEFT CLICK
            if (this.creationController.currentTool.name == "Text") {
              this.creationController.stage.container().style.cursor = "wait";
            }
            this.creationController.currentTool.mouseDown(this.creationController);
            break;

          default:
            break;
        }
      }
    },
    stageMouseUp(event) {
      if (this.getIsLoading) {
        return;
      }

      const cursor = this.creationController.stage.container().style.cursor;
      if (cursor == "pointer" || cursor == "crosshair" || (event.evt.button == 1 && cursor == "move")) {
        this.creationController.stage.container().style.cursor = "default";
      } else if (cursor == "grabbing") {
        this.creationController.stage.container().style.cursor = "grab";
      }

      if (this.mouseDown) {
        if (this.isStageMoving) {
          this.isStageMoving = false;
          this.creationController.mouseEventTrigger("moveStageEnd", event);
        }

        if (event.evt.button == 0 && this.creationController.currentTool.type == "widget") {
          const objectName = this.creationController.currentTool.mouseUp(this.creationController);

          if (objectName != null) {
            this.layers.unshift(objectName);
            this.currentObjectOption = this.creationController.currentElement.konvaObject.attrs;
            this.creationController.currentTool = {
              name: "pointer",
              type: "basic",
            };
            //this.layersMoved(this.layers);
            this.creationController.enableDraggable();
          } else {
            // TODO: maybe we need to make a maximum attempt
            // Object name is not defined yet so we retry later
            setTimeout(() => {
              this.stageMouseUp(event);
            }, 500);

            return;
          }
        }
      }

      this.mouseDown = false;
    },
    stageMouseLeave(val) {
      this.creationController.stage.container().style.cursor = "pointer";

      const evt = {
        evt: val,
        type: "mouseup",
      };

      this.stageMouseUp(evt);
    },
    toolSelected(tool) {
      this.creationController.stage.container().style.cursor = "default";
      this.creationController.selectTool(tool);

      if (tool != null && tool.type == "widget") {
        this.creationController.destroyAllTransformer();
        this.creationController.disableDraggable();
        this.focusOption(this.creationController.currentTool.currentOptions);

        try {
          this.creationController.stage.draw();
        } catch (err) {
          console.error(err);
        }
      } else {
        this.creationController.enableDraggable();
      }

      this.zoomTool(tool);
      this.cursor(tool);
      if (tool.type !== "basic") {
        this.unsavedChange = true;
      }
    },
    zoomTool(tool) {
      if (tool.type == "basic") {
        let event = "";
        switch (tool.name) {
          case "zoomPlus":
            event = { evt: { deltaY: -100 } };
            break;
          case "zoomMinus":
            event = { evt: { deltaY: 100 } };
            break;
          case "zoomResize":
            this.onResize();
            this.creationController.stage.container().style.cursor = "default";
            break;
          case "move":
            this.creationController.stage.container().style.cursor = "move";
            break;

          default:
            break;
        }
        if (event != "") {
          this.creationController.mouseEventTrigger("zoom", event);
        }
      }
    },
    cursor(tool) {
      switch (tool.name) {
        case "Rect":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "Text":
          this.creationController.stage.container().style.cursor = "text";
          break;
        case "Scrolling":
          this.creationController.stage.container().style.cursor = "text";
          break;
        case "Image":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "pdf":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "Video":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "rss":
          this.creationController.stage.container().style.cursor = "text";
          break;
        case "Calendar":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "Button":
          this.creationController.stage.container().style.cursor = "crosshair";
          break;
        case "move":
          this.creationController.stage.container().style.cursor = "move";
          break;

        default:
          if (this.creationController.stage !== undefined)
            this.creationController.stage.container().style.cursor = "default";
          break;
      }
    },
    updateCurrentObjectFromOptions(x, y, width, height, scaleX, scaleY) {
      if (this.mouseDown) {
        return;
      }
      if (this.creationController.currentElement == undefined || x === undefined) {
        this.currentObjectOption = undefined;
        return;
      }
      this.creationController.currentElement.konvaObject.setAttr("x", checkValueInt(x, 0));
      this.creationController.currentElement.konvaObject.setAttr("y", checkValueInt(y, 0));
      this.creationController.currentElement.konvaObject.setAttr("scaleX", checkValueFloat(scaleX));
      this.creationController.currentElement.konvaObject.setAttr("scaleY", checkValueFloat(scaleY));

      if (this.isPartialRectOption) {
        return;
      }

      this.creationController.currentElement.konvaObject.setAttr("width", checkValueInt(width, 0));
      this.creationController.currentElement.konvaObject.setAttr("height", checkValueInt(height, 0));

      this.unsavedChange = true;
    },
    zoom(event) {
      if (!this.creationController.ctrlKeyPressed && !this.creationController.altKeyPressed) {
        this.creationController.mouseEventTrigger("moveStageVertic", event);
      }

      if (this.creationController.ctrlKeyPressed) {
        this.creationController.mouseEventTrigger("moveStageHoriz", event);
      }

      if (this.creationController.altKeyPressed) {
        this.creationController.mouseEventTrigger("zoom", event);
      }

      event.evt.preventDefault();
    },
    cloned(objectToClone) {
      let newDMCObjectId = objectToClone.clone(this.creationController);
      this.layers.push(newDMCObjectId);
    },
  },
};
</script>

<style>
.creation-page-workspace {
  transform-origin: top left;
  width: 100%;
  height: 100%;
}

.creation-page-workspace-container {
  overflow-x: hidden;
  overflow-y: hidden;
  max-height: 75vh;
}

.creation-page-workspace .konvajs-content {
  transform-origin: top left;
}

.creation-toolbar {
  max-width: 70px;
  min-width: 70px;
}

/**
    Text rendering container
  */
#textRenderingToCanvas {
  position: absolute;
  top: -9999px;
  left: -9999px;

  background-color: transparent;
  font-size: 400%;
  white-space: nowrap;
  word-wrap: none;
  opacity: 0;

  font-family: "Roboto", sans-serif;
  line-height: 1em;
}

/** 
    Player container
  */
#preview-project .v-overlay__content {
  width: 100%;
  height: 100%;
}

/**
    Better alpha background on color picker
  */
.v-color-picker__alpha {
  background-color: white;
}
</style>
